import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';

export type RoleCountResult = {
  contributorCount: number;
  roleCount: number;
  roleHeatmap: Record<string, { accepted: number; total: number }>;
};

/**
 * Counts the number of contributors and roles matching the specified role types
 * and generates a heatmap of role counts with accepted vs total breakdown
 *
 * @param event The event containing contributors and roles
 * @param roleTypes Array of role types to check against
 * @returns Object with contributor count, role count, and role heatmap showing accepted/total counts
 */
export const useRoleCount = (
  contributors: ContributorDto[] | null | undefined,
  roleTypes: readonly string[],
): RoleCountResult => {
  if (!contributors) {
    return { contributorCount: 0, roleCount: 0, roleHeatmap: {} };
  }

  const matchingRoles = (contributors ?? []).flatMap((contributor) =>
    contributor.roles.filter((role) => (roleTypes as readonly string[]).includes(role.role as string)),
  );

  const contributorCount = (contributors ?? []).filter((contributor) =>
    contributor.roles.some((role) => (roleTypes as readonly string[]).includes(role.role as string)),
  ).length;

  const roleHeatmap = matchingRoles.reduce<Record<string, { accepted: number; total: number }>>((heatmap, role) => {
    const roleType = role.role as string;

    if (!heatmap[roleType]) {
      heatmap[roleType] = { accepted: 0, total: 0 };
    }

    heatmap[roleType].total += 1;

    if (role.acceptedAt) {
      heatmap[roleType].accepted += 1;
    }

    return heatmap;
  }, {});

  return {
    contributorCount,
    roleCount: matchingRoles.length,
    roleHeatmap,
  };
};
